// Wire protocol, shared by the client and the server (PLAN §5.5).
//
// JSON rather than protobuf, deliberately: this ships one small typed command
// per turn, a few hundred bytes, so a binary encoding would be pure ceremony
// (PLAN §5.1). Tauric needs protobuf because it ships binary Yjs blobs.

import type { Command } from "src/engine/rules/commands";

export type Seat = "white" | "black" | "spectator";

export const SEAT_COLOR: Record<Exclude<Seat, "spectator">, 0 | 1> = {
  white: 0,
  black: 1
};

/** Who is in the room, as everyone in it sees them. */
export interface Occupant {
  seat: Seat;
  name: string;
}

export type ClientMessage =
  | { t: "join"; room: string; seat?: Seat; name?: string; build?: string }
  | { t: "cmd"; seq: number; command: Command; hash: number }
  /**
   * Take back my last action this turn (§29 U3).
   *
   * No payload: the server runs the same `planUndo` the greyed button ran, so
   * a client naming an index would be naming one the room may already have
   * moved past.
   */
  | { t: "undo" };

export type ServerMessage =
  /**
   * Room state at join time: replay `log` from a fresh game to catch up.
   *
   * `build` is the server's own, so a client can tell a **stale tab** from a
   * determinism bug. Both run the same reducer over the same log, so the only
   * way they can disagree is by running different *copies* of it — which is
   * the normal state of affairs for a few minutes after every deploy, since a
   * browser holds its bundle until it is reloaded. Without this the two are
   * indistinguishable, and the one that is not a bug is by far the commoner.
   */
  | {
      t: "joined";
      room: string;
      seat: Seat;
      log: Command[];
      seq: number;
      name: string;
      build: string;
    }
  /** Who is in the room. Sent on every arrival and departure. */
  | { t: "roster"; occupants: Occupant[] }
  /** An accepted command, with the *server's* hash after applying it. */
  | { t: "cmd"; seq: number; command: Command; hash: number }
  /** The server's reducer refused it; it was never broadcast. */
  | { t: "rejected"; seq: number; reason: string }
  /**
   * The cut the room made, for everyone to make (§29 U3): the indices that
   * left the log, the length it is now, and the server's hash after it.
   *
   * **The indices and never the resulting log** — one implementation of the
   * cut decides, and a log on the wire would be a second one to disagree with.
   */
  | { t: "undo"; dropped: number[]; seq: number; hash: number }
  /**
   * Undo was refused. **Not `rejected`**, which rebuilds from `seq - 1`: an
   * undo request applies nothing optimistically, so there is nothing to undo.
   */
  | { t: "undo-refused"; reason: string }
  | { t: "error"; reason: string };

/**
 * Names are display text from a stranger, so they are clamped before anything
 * shows them: length, no control characters, and a fallback for a name that
 * is empty once trimmed. Done on the *server*, because a client is free to
 * send whatever it likes and every other client has to render it.
 */
export const MAX_NAME_LENGTH = 24;

export function cleanName(raw: string | undefined, fallback: string): string {
  if (typeof raw !== "string") return fallback;
  // eslint-disable-next-line no-control-regex
  const stripped = raw.replace(/[\u0000-\u001f\u007f]/g, "").trim();
  return stripped.length > 0 ? stripped.slice(0, MAX_NAME_LENGTH) : fallback;
}

/**
 * What a client and server compare to know they are the same build.
 *
 * The client's comes from `__BUILD_ID__` at bundle time; the server reads the
 * `version.json` that the *same* Vite build emitted, so on a real deployment
 * the two agree by construction rather than by two environment variables being
 * set the same way. Unknown on either side reads as "cannot tell", which is
 * treated as agreement — a dev server with no build output must not present
 * every game as stale.
 */
export const BUILD_UNKNOWN = "unknown";

export const buildsAgree = (a: string | undefined, b: string | undefined): boolean =>
  !a || !b || a === BUILD_UNKNOWN || b === BUILD_UNKNOWN || a === b;

export const WS_PATH = "/ws";

/** Local dev port; production shares the HTTP port Railway injects. */
export const DEV_WS_PORT = 3221;

/**
 * Same-origin socket URL, upgrading to wss: when the page is https:.
 *
 * Deriving it from the page rather than from a build-time variable is the main
 * reason the whole thing ships as one Railway service — there is no
 * environment config to get wrong across local, preview and production.
 */
export function socketUrl(location: { protocol: string; host: string }): string {
  const scheme = location.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${location.host}${WS_PATH}`;
}
